import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'underscore';
import {
  FluentProvider,
  webLightTheme,
  RadioGroup,
  Radio,
} from '@fluentui/react-components';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { BudgetItemLabel, CustomBudgetLabel } from './budget-item';
import { RecommendedBudgetSuggestionTier, getMatchingBudgetTierBasedOnBudget, CustomBudget } from '../model/constants';

const getStyles = () => ({
  selectedRadio: {
    border: '1px solid rgba(15, 108, 189, 1)',
    padding: '12px, 16px, 12px, 8px',
    borderRadius: '4px',
  },
  radioGroupContainer: {
    gap: '16px',
    width: '100%',
  },
  currentBudget: {
    paddingLeft: '32px',
    gap: '16px',
    borderBottom: '1px solid var(--Neutral-Stroke-2-Rest, #E0E0E0)',
  },
  budgetTitle: {
    fontSize: '16px',
    fontWeight: '600',
  },
  radio: {
    width: '100%',
    '& .fui-Label': {
      width: '100%',
    },
  },
});

export const BudgetSuggestionOfflineContainer = ({
  data,
  isLoading,
  mode,
  campaignType,
  currency,
  onBudgetValidate,
  defaultBudget,
  bidType,
  minBudget,
  maxBudget,
}) => {
  const classes = useDefaultStyles(getStyles);
  const i18n = useContext(I18nContext);

  const [selectedBudget, setSelectedBudget] = useState(null);

  const [customBudgetResult, setCustomBudgetResult] = useState(null);

  useEffect(() => {
    if (!isLoading) {
      if (selectedBudget !== CustomBudget) {
        const BudgetAmount = data && data[selectedBudget] && data[selectedBudget].BudgetAmountDaily;

        onBudgetValidate({ data: { budget: BudgetAmount, budgetTier: selectedBudget }, isValid: true });
      } else if (customBudgetResult) {
        onBudgetValidate({ data: { ...customBudgetResult.data, budgetTier: selectedBudget }, isValid: customBudgetResult.isValid, messages: customBudgetResult.messages });
      }
    }
  }, [customBudgetResult, data, isLoading, onBudgetValidate, selectedBudget]);

  useEffect(() => {
    const matchingTier = getMatchingBudgetTierBasedOnBudget(data, defaultBudget, mode);

    if (selectedBudget === null && data && defaultBudget) {
      setSelectedBudget(matchingTier);
    } else if (selectedBudget === null && data) {
      setSelectedBudget(RecommendedBudgetSuggestionTier);
    }
  }, [defaultBudget, data, selectedBudget, mode]);

  if (!isLoading && _.isEmpty(data)) {
    return (
      <div>
        {i18n.getString(_TL_('There are no budget suggestions available for this campaign.'))}
      </div>
    );
  }

  return (
    <FluentProvider theme={webLightTheme}>
      <div className="budget-suggestion-group">
        <div className={classes.currentBudget}>
          <BudgetItemLabel
            tier="Current"
            data={data && data.Current}
            isLoading={isLoading}
            campaignType={campaignType}
            bidType={bidType}
            currency={currency}
          />
        </div>
        <RadioGroup label="Choose an option" onChange={(e, val) => setSelectedBudget(val.value)} className={classNames(classes.radioGroupContainer, isLoading ? 'budget-loading' : '')}>
          <span className={classes.budgetTitle}>
            {i18n.getString(_TL_('Select new budget'))}
          </span>
          <Radio
            key={RecommendedBudgetSuggestionTier}
            value={RecommendedBudgetSuggestionTier}
            label={<BudgetItemLabel
              tier={RecommendedBudgetSuggestionTier}
              data={data && data.Suggested}
              isSelected={selectedBudget === RecommendedBudgetSuggestionTier}
              currency={currency}
              isLoading={isLoading}
              mode={mode}
              campaignType={campaignType}
              bidType={bidType}
            />}
            checked={selectedBudget === RecommendedBudgetSuggestionTier}
            className={
              selectedBudget === RecommendedBudgetSuggestionTier ?
                classNames(classes.selectedRadio, classes.radio, 'budget-radio-Suggested')
                : classNames('budget-radio-Suggested', classes.radio)}
          />
          <Radio
            key="CustomBudget"
            value="CustomBudget"
            label={<CustomBudgetLabel
              isSelected={selectedBudget === CustomBudget}
              isLoading={isLoading}
              campaignType={campaignType}
              onUpdateCustomBudget={setCustomBudgetResult}
              hidePerformanceData
              defaultBudget={defaultBudget}
              currency={currency}
              customBudgetResult={customBudgetResult}
              bidType={bidType}
              minBudget={minBudget}
              maxBudget={maxBudget}
            />}
            checked={selectedBudget === CustomBudget}
            className={selectedBudget === CustomBudget ?
              classNames(classes.selectedRadio, classes.radio, 'budget-radio-CustomBudget')
              : classNames('budget-radio-CustomBudget', classes.radio)}
          />
        </RadioGroup>
      </div>
    </FluentProvider>
  );
};

BudgetSuggestionOfflineContainer.propTypes = {
  data: PropTypes.shape({
    Current: PropTypes.shape({
      BudgetAmountDaily: PropTypes.number,
      BudgetAmountMonthly: PropTypes.number,
    }),
    Suggested: PropTypes.shape({
      BudgetAmountDaily: PropTypes.number,
      BudgetAmountMonthly: PropTypes.number,
    }),
  }).isRequired,
  isLoading: PropTypes.bool.isRequired,
  mode: PropTypes.string.isRequired,
  campaignType: PropTypes.string.isRequired,
  onBudgetValidate: PropTypes.func.isRequired,
  defaultBudget: PropTypes.number.isRequired,
};
